import React, { useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonToast,
  IonLoading,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonProgressBar,
} from "@ionic/react";
import {
  eyeOutline,
  eyeOffOutline,
  mailOutline,
  callOutline,
  lockClosedOutline,
  personOutline,
  personAddOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ApiService } from "../services/api.service";
import "./Signup.css";

interface SignupProps {
  onSignupSuccess?: () => void;
}

const Signup: React.FC<SignupProps> = ({ onSignupSuccess }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [emailOrMobile, setEmailOrMobile] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastColor, setToastColor] = useState<string>("primary");

  const getPasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength < 25) return "danger";
    if (strength < 50) return "warning";
    if (strength < 75) return "primary";
    return "success";
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength < 25) return t("signup.passwordStrength.weak");
    if (strength < 50) return t("signup.passwordStrength.fair");
    if (strength < 75) return t("signup.passwordStrength.good");
    return t("signup.passwordStrength.strong");
  };

  const handleSignup = async () => {
    // Validation
    if (!firstName.trim()) {
      setToastMessage(t("signup.validation.firstName"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    if (!lastName.trim()) {
      setToastMessage(t("signup.validation.lastName"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    if (!isValidEmailOrMobile(emailOrMobile)) {
      setToastMessage(t("signup.validation.emailOrMobile"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    if (password.length < 6) {
      setToastMessage(t("signup.validation.password"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    if (password !== confirmPassword) {
      setToastMessage(t("signup.validation.confirmPassword"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    if (!agreeToTerms) {
      setToastMessage(t("signup.validation.terms"));
      setToastColor("danger");
      setShowToast(true);
      return;
    }

    setIsLoading(true);

    try {
      // Prepare signup data
      const signupData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailOrMobile: emailOrMobile.trim(),
        password: password,
        agreeToTerms: agreeToTerms,
      };

      console.log("Attempting signup with data:", {
        ...signupData,
        password: "***hidden***", // Hide password in logs
      });

      // Make API call to backend
      const response = await ApiService.post("/signup", signupData);
      // Handle successful response
      if (response.data && response.status === 201) {
        // Store authentication token
        if (response.data.authToken) {
          ApiService.setAuthToken(response.data.authToken);
        }

        // Store user data in localStorage for app use
        if (response.data.user) {
          localStorage.setItem(
            "userData",
            JSON.stringify({
              userId: response.data.user.userId,
              firstName: response.data.user.firstName,
              lastName: response.data.user.lastName,
              emailOrMobile: response.data.user.emailOrMobile,
            })
          );
        }

        setToastMessage(response.data.message || t("signup.success"));
        setToastColor("success");
        setShowToast(true);

        console.log("Signup successful:", response.data);

        // Navigate to home page after successful signup
        setTimeout(() => {
          if (onSignupSuccess) {
            onSignupSuccess();
          }
          history.push("/tab1");
        }, 1500);
      } else {
        // Handle unsuccessful response
        throw new Error(response.data?.message || "Signup failed");
      }
    } catch (error: any) {
      console.error("Signup error:", error);

      // Handle specific error cases
      let errorMessage = t("signup.error");

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 409) {
        errorMessage = t("signup.errors.userExists");
      } else if (error.response?.status === 400) {
        errorMessage = t("signup.errors.invalidData");
      } else if (error.response?.status >= 500) {
        errorMessage = t("signup.errors.serverError");
      } else if (error.code === "NETWORK_ERROR" || !navigator.onLine) {
        errorMessage = t("signup.errors.networkError");
      }

      setToastMessage(errorMessage);
      setToastColor("danger");
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmailOrMobile = (input: string): boolean => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Mobile number validation regex (supports various formats)
    // Matches: +1234567890, 1234567890, +91-1234567890, etc.
    const mobileRegex = /^(\+\d{1,3}[-.\s]?)?\d{10,15}$/;

    return (
      emailRegex.test(input) || mobileRegex.test(input.replace(/[-.\s]/g, ""))
    );
  };

  const handleSignIn = () => {
    history.push("/login");
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <IonPage>
      <IonContent className="signup-content">
        <div className="signup-container">
          <IonCard className="signup-card">
            <IonCardHeader className="signup-header">
              <IonCardTitle className="signup-title">
                {t("signup.title")}
              </IonCardTitle>
              <IonText color="medium" className="signup-subtitle">
                {t("signup.subtitle")}
              </IonText>
            </IonCardHeader>

            <IonCardContent className="signup-card-content">
              {/* First Name and Last Name Row */}
              <IonGrid className="signup-grid">
                <IonRow>
                  <IonCol size="6">
                    <div className="input-group">
                      <IonLabel className="input-label">
                        {t("signup.firstName")}
                      </IonLabel>
                      <IonItem lines="none" className="signup-input-item">
                        <IonIcon
                          icon={personOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonInput
                          type="text"
                          placeholder={t("signup.firstNamePlaceholder")}
                          value={firstName}
                          onIonInput={(e) => setFirstName(e.detail.value!)}
                          className="signup-input"
                        />
                      </IonItem>
                    </div>
                  </IonCol>
                  <IonCol size="6">
                    <div className="input-group">
                      <IonLabel className="input-label">
                        {t("signup.lastName")}
                      </IonLabel>
                      <IonItem lines="none" className="signup-input-item">
                        <IonIcon
                          icon={personOutline}
                          slot="start"
                          color="medium"
                        />
                        <IonInput
                          type="text"
                          placeholder={t("signup.lastNamePlaceholder")}
                          value={lastName}
                          onIonInput={(e) => setLastName(e.detail.value!)}
                          className="signup-input"
                        />
                      </IonItem>
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>

              {/* Email or Mobile Number */}
              <div className="input-group">
                <IonLabel className="input-label">
                  {t("signup.emailOrMobile")}
                </IonLabel>
                <IonItem lines="none" className="signup-input-item">
                  <IonIcon
                    icon={
                      isValidEmailOrMobile(emailOrMobile) &&
                      emailOrMobile.includes("@")
                        ? mailOutline
                        : callOutline
                    }
                    slot="start"
                    color="medium"
                  />
                  <IonInput
                    type="text"
                    placeholder={t("signup.emailOrMobilePlaceholder")}
                    value={emailOrMobile}
                    onIonInput={(e) => setEmailOrMobile(e.detail.value!)}
                    className="signup-input"
                  />
                </IonItem>
              </div>

              {/* Password */}
              <div className="input-group">
                <IonLabel className="input-label">
                  {t("signup.password")}
                </IonLabel>
                <IonItem lines="none" className="signup-input-item">
                  <IonIcon
                    icon={lockClosedOutline}
                    slot="start"
                    color="medium"
                  />
                  <IonInput
                    type={showPassword ? "text" : "password"}
                    placeholder={t("signup.passwordPlaceholder")}
                    value={password}
                    onIonInput={(e) => setPassword(e.detail.value!)}
                    className="signup-input"
                  />
                  <IonButton
                    fill="clear"
                    slot="end"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <IonIcon
                      icon={showPassword ? eyeOffOutline : eyeOutline}
                      color="medium"
                    />
                  </IonButton>
                </IonItem>

                {/* Password Strength Indicator */}
                {password && (
                  <div className="password-strength">
                    <IonText color="medium" className="password-strength-label">
                      {t("signup.passwordStrength.strength")}:
                    </IonText>
                    <IonProgressBar
                      value={passwordStrength / 100}
                      color={getPasswordStrengthColor(passwordStrength)}
                      className="password-strength-bar"
                    />
                    <IonText
                      color={getPasswordStrengthColor(passwordStrength)}
                      className="password-strength-text"
                    >
                      {getPasswordStrengthText(passwordStrength)}
                    </IonText>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="input-group">
                <IonLabel className="input-label">
                  {t("signup.confirmPassword")}
                </IonLabel>
                <IonItem lines="none" className="signup-input-item">
                  <IonIcon
                    icon={lockClosedOutline}
                    slot="start"
                    color="medium"
                  />
                  <IonInput
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("signup.confirmPasswordPlaceholder")}
                    value={confirmPassword}
                    onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                    className="signup-input"
                  />
                  <IonButton
                    fill="clear"
                    slot="end"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <IonIcon
                      icon={showConfirmPassword ? eyeOffOutline : eyeOutline}
                      color="medium"
                    />
                  </IonButton>
                </IonItem>
              </div>

              {/* Terms and Conditions */}
              <div className="terms-section">
                <IonItem lines="none" className="terms-item">
                  <IonCheckbox
                    checked={agreeToTerms}
                    onIonChange={(e) => setAgreeToTerms(e.detail.checked)}
                    slot="start"
                  />
                  <IonLabel className="terms-label">
                    <IonText>
                      {t("signup.agreeToTerms")}{" "}
                      <span className="terms-link">
                        {t("signup.termsOfService")}
                      </span>{" "}
                      {t("signup.and")}{" "}
                      <span className="privacy-link">
                        {t("signup.privacyPolicy")}
                      </span>
                    </IonText>
                  </IonLabel>
                </IonItem>
              </div>

              {/* Create Account Button */}
              <IonButton
                expand="block"
                size="large"
                className="create-account-btn"
                onClick={handleSignup}
                disabled={isLoading}
              >
                <IonIcon icon={personAddOutline} slot="start" />
                {t("signup.createAccount")}
              </IonButton>

              {/* Sign In Link */}
              <div className="signin-section">
                <IonText color="medium">{t("signup.haveAccount")}</IonText>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={handleSignIn}
                  className="signin-btn"
                >
                  {t("signup.signIn")}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        </div>

        <IonLoading isOpen={isLoading} message={t("signup.creating")} />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
        />
      </IonContent>
    </IonPage>
  );
};

export default Signup;
